import * as React from 'react'
import cx from 'classnames'
import Spinner from 'components/Spinner'
import { NavLink, NavLinkProps } from 'react-router-dom'
import styles from './IconButton.sass'

/**
 * Just in case `NavLink` isn't forwarded.
 */
const ForwardedLink = React.forwardRef((props: NavLinkProps<any>, ref) => (
  <NavLink {...props} ref={ref as any} />
))

interface IconButtonProps
  extends React.HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
  children?: React.ReactNode
  className?: string
  disabled?: boolean
  loading?: boolean
  submit?: boolean
  title?: string
  to?: string
  exact?: boolean
  href?: string
  inheritColor?: boolean
  variant?: IconButtonVariant
  inline?: boolean
  dataTest?: string
}

export const IconButton = React.forwardRef(function IconButton(
  {
    children,
    className,
    disabled,
    loading,
    submit,
    to,
    exact,
    href,
    style,
    inheritColor,
    variant,
    onClick,
    dataTest,
    inline,
    ...rest
  }: IconButtonProps,
  ref: React.Ref<any>
) {
  const Element = submit ? 'button' : to ? (ForwardedLink as any) : 'a'
  return (
    <Element
      ref={ref}
      role="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      {...(Element === ForwardedLink
        ? { to, exact }
        : Element === 'a'
        ? { href }
        : undefined)}
      className={cx(
        styles.button,
        loading && styles.loading,
        disabled && styles.disabled,
        inline && styles.inline,
        variant && styles[variant],
        className
      )}
      style={{
        color: inheritColor ? 'inherit' : undefined,
        ...style,
      }}
      data-test={dataTest}
      {...rest}
    >
      {loading ? <Spinner size={24} /> : children}
    </Element>
  )
})

export type IconButtonVariant = 'ghost'
